import { z } from "zod";

import { createCareplanDataModel } from "@/domains/careplan/factories/create-careplan-data-model";
import {
  CAREPLAN_CHANGE_ATTRIBUTES,
  CAREPLAN_PRODUCT_FORMAT,
  CAREPLAN_PRODUCT_ROLE,
  DISCOUNT_CODE_ACTIONS,
} from "@/domains/careplan/models/constants";
import { progressTeaserSchema } from "@/domains/progress/models/schemas";
import { subscriptionStatusSchema } from "@/domains/subscriptions/models/schemas";

export const careplanProductRoleSchema = z.enum(CAREPLAN_PRODUCT_ROLE);
export const careplanProductFormatSchema = z.enum(CAREPLAN_PRODUCT_FORMAT);
const careplanChangeAttributeSchema = z.enum(CAREPLAN_CHANGE_ATTRIBUTES);

export const careplanChangeSchema = z.object({
  attribute: careplanChangeAttributeSchema,
  entity: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  role: careplanProductRoleSchema,
});

export const careplanProductResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  allIngredients: z.string(),
  brand: z.string(),
  cannaboidContent: z.string().nullable(),
  cbdThcRatio: z.string().nullable(),
  description: z.string(),
  dose: z.string(),
  effectDuration: z.string().nullable(),
  effectOnset: z.string().nullable(),
  facts: z.string().nullable(),
  form: z.string().nullable(),
  forms: careplanProductFormatSchema.nullable(),
  guaranteedSatisfaction: z.string().nullable(),
  howAndWhereMade: z.string().nullable(),
  images: z.array(z.string()),
  /**
    A simpler cutout for the plan's product list, from a Shopify product metafield that is not
    configured yet. Null until it is, and the list falls back to the first of `images`.
  */
  summaryImage: z.string().nullish().default(null),
  increaseDose: z.string().nullable(),
  price: z.number(),
  productSupply: z.string().nullable(),
  role: careplanProductRoleSchema,
  shippingPolicy: z.string().nullable(),
  sku: z.string(),
  symptoms: z.string().nullable(),
  thcCbdType: z.string().nullable(),
  timesOfUse: z.string().nullable(),
  type: z.string(),
  usageInstructions: z.string().nullable(),
  whyRecommended: z
    .object({
      title: z.string(),
      content: z.string(),
    })
    .nullable(),
  howItWorks: z.string().nullable(),
  importantSafetyNotes: z.string().optional(),
  variantId: z.string().nullable(),
  /* Okendo review aggregate; optional so the page never breaks without it. */
  rating: z.number().nullish().default(null),
  reviewCount: z.number().int().nullish().default(null),
});

export const careplanProductDataModelSchema = careplanProductResponseSchema
  .extend({
    changes: z.array(careplanChangeSchema).optional(),
    isPrevious: z.boolean(),
    priceAsString: z.string(),
    priceInCents: z.number(),
    format: careplanProductFormatSchema.nullable(),
    details: z.array(
      z.object({
        id: z.number(),
        title: z.string(),
        slug: z.string(),
        shortTitle: z.string().optional(),
        body: z.string().nullable(),
      }),
    ),
  })
  .omit({ price: true });

export const previousCareplanProductResponseSchema = z.object({
  description: z.string(),
  id: z.number(),
  variantId: z.string(),
  image: z.string(),
  name: z.string(),
  price: z.number(),
  role: careplanProductRoleSchema,
  whyRecommended: z.string(),
});

export const previousCareplanProductDataModelSchema = previousCareplanProductResponseSchema.extend({
  images: z.array(z.string()),
  priceAsString: z.string(),
  whyRecommended: z.object({
    title: z.string(),
  }),
  isPrevious: z.boolean(),
});

export const subscriptionProductResponseSchema = z.object({
  finalPrice: z.number(),
  id: z.number(),
  variantId: z.string(),
  url: z.string(),
  price: z.number(),
  quantity: z.number(),
  title: z.string(),
  sku: z.string(),
  sellingPlanId: z.number().nullable(),
});

export const subscriptionResponseSchema = z.object({
  status: subscriptionStatusSchema,
  acceptedAt: z.string().nullable(),
  rejectedAt: z.string().nullable(),
  nextBillingDate: z.string().nullable(),
  shippingAddress: z.string().nullable().default(null),
  paymentMethod: z.object({
    cardBrand: z.string().nullable(),
    expiryDate: z.string().nullable(),
    lastDigits: z.string().nullable(),
  }),
  products: z.array(subscriptionProductResponseSchema),
});

const careplanResponseSchema = z.object({
  id: z.number(),
  version: z.number(),
  products: z.array(careplanProductResponseSchema),
  readAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  wasBuiltFromSurvey: z.boolean(),
  subscriptions: z.array(subscriptionResponseSchema),
});

const previousCareplanResponseSchema = careplanResponseSchema
  .pick({ id: true, products: true })
  .extend({
    products: z.array(previousCareplanProductResponseSchema),
  });

export const careplansResponseSchema = z.object({
  current: careplanResponseSchema,
  previous: previousCareplanResponseSchema.nullable(),
  /** Forgiving on purpose: a bad teaser costs one card, never the patient's main screen. */
  progressTeaser: progressTeaserSchema.nullish().catch(null),
  progressEnabled: z.boolean().catch(false),
});

export const careplanMetaSchema = z.object({
  changes: z.array(careplanChangeSchema),
});

export const careplanDataModelSchema = careplansResponseSchema.transform(createCareplanDataModel);

export const discountCodeActionSchema = z.enum(DISCOUNT_CODE_ACTIONS);

export const toggleDiscountCodeFormSchema = z.object({
  code: z.string().min(1, "Discount code cannot be empty"),
  action: discountCodeActionSchema,
});
